/**
 * Normaliza um número de telefone removendo todos os caracteres não numéricos
 * @param {string} phone - Número de telefone a normalizar
 * @param {string} countryCode - Código do país selecionado (ex: '351', '49', '33'). Padrão: '351' (Portugal)
 * @returns {string} - Telefone normalizado (apenas números)
 */
export const normalizePhone = (phone, countryCode = '351') => {
  // garantir string
  let digits = (phone ?? '').toString().replace(/\D/g, '');
  if (!digits) return '';

  // remover prefixo internacional "00"
  if (digits.startsWith('00')) digits = digits.slice(2);

  // Se começa com código do país selecionado, remover
  if (digits.startsWith(countryCode) && digits.length > countryCode.length) {
    digits = digits.slice(countryCode.length);
  }

  // Para Portugal (351): remover zero inicial se houver (ex: 0912345678 -> 912345678)
  if (countryCode === '351') {
    if (digits.length === 10 && digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    // Para PT: garantir 9 dígitos (formato padrão português)
    if (digits.length > 9) digits = digits.slice(-9);
  } else {
    // Para outros países: tentar normalizar mantendo número válido
    // Remover zero inicial se houver (comum em alguns países)
    if (digits.length > 6 && digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    // Não truncar arbitrariamente para países internacionais
    // Manter número como está após remover código do país
  }

  return digits;
};

/**
 * Formata o telefone com código do país para exibição
 * @param {string} phone - Número normalizado
 * @param {string} countryCode - Código do país
 * @returns {string} - Telefone formatado (ex: +351 912 345 678)
 */
export const formatPhoneWithCountryCode = (phone, countryCode = '351') => {
  if (!phone) return '';
  const normalized = normalizePhone(phone, countryCode);
  if (countryCode === '351' && normalized.length === 9) {
    return `+${countryCode} ${normalized.slice(0, 3)} ${normalized.slice(3, 6)} ${normalized.slice(6)}`;
  }
  return `+${countryCode} ${normalized}`;
};

/**
 * Extrai o código do país de um número de telefone
 * @param {string} phone - Número de telefone (pode ter + ou 00 no início)
 * @returns {object} - { countryCode: string, phoneNumber: string } ou null se não conseguir extrair
 */
export const extractCountryCode = (phone) => {
  if (!phone || typeof phone !== 'string') return { countryCode: '351', phoneNumber: '' };
  
  // Lista de códigos de país comuns (ordenados por tamanho, maiores primeiro para evitar matches incorretos)
  const countryCodes = [
    '351', '49', '33', '31', '44', '34', '39', '41', '32', '45', '46', '47', '358', '351', '49'
  ];
  
  // Remover espaços e caracteres especiais exceto + e números
  let cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // Se começa com +, remover
  if (cleanPhone.startsWith('+')) {
    cleanPhone = cleanPhone.substring(1);
  }
  
  // Se começa com 00, remover
  if (cleanPhone.startsWith('00')) {
    cleanPhone = cleanPhone.substring(2);
  }
  
  // Tentar encontrar código do país
  for (const code of countryCodes) {
    if (cleanPhone.startsWith(code) && cleanPhone.length > code.length) {
      return {
        countryCode: code,
        phoneNumber: cleanPhone.substring(code.length)
      };
    }
  }
  
  // Se não encontrou código, assumir Portugal como padrão
  return {
    countryCode: '351',
    phoneNumber: cleanPhone
  };
};

